import { today } from 'owa-datetime';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste
 *	> 'createAccessors' import from 'owa-viewstate-store' is restricted. createAccessors can cause performance problems. Create mutators by hand */
import { createAccessors } from 'owa-viewstate-store';
import { createStore } from 'owa-satcheljs';
import { mutatorAction } from 'satcheljs';

import type { AppDefinition, CollaboratorGroup } from 'owa-graph-schema';
import type { OwaDate } from 'owa-datetime';
import type { UserEmailAddress } from 'accelerator-aad/lib/UserEmailAddress';
import type { WorkLocationInfo } from 'places-explore-operations';

/**
 * this store is used to track values that are shared among
 * components in places-explore
 */
const getStore = createStore('places-explore-app-store', {
    currentDate: null as OwaDate | null,
    isMapAvailable:
        true /** Map availability has some complex logic, so instead of recalculating it, storing here */,
    recentBuildings: null as string[] | null, // Map of recent buildings the user has visited, key is building id and value is last visited date
    workLocationInfo: null as WorkLocationInfo | null, // this is user's FWH info, changes to this default value will break call to loadWorkLocationInfo
    collaboratorsLocations: null as Map<
        string,
        Map<CollaboratorGroup, UserEmailAddress[]>
    > | null /* converting to null/unknown first to know when data has been loaded already
        Stores collaborators with location info with building id as a key.
        ex. < BUILDING_ID : <'Saved' : [EMAIL_ADDRESS]>> */,
    rareVisitData: new Map<UserEmailAddress, string[]>(), // Map of rare visit data, key is collaborators' email and value is array of building Id's
    shouldFilterByRareVisits: false,
    buildingAppDefinitions: new Map<string, AppDefinition[]>(),
});

export function getCurrentDate(): OwaDate {
    // We cannot initialize a store with "today" because there is no guarantee
    // a store will be initialized before or after user time zone is loaded and
    // today() depends on the user's time zone being initialized.
    return getCurrentDateOrNull() ?? today();
}

export const store: {
    currentDate: OwaDate | null;
    isMapAvailable: boolean;
    /** Map availability has some complex logic, so instead of recalculating it, storing here */ recentBuildings:
        | string[]
        | null; // Map of recent buildings the user has visited, key is building id and value is last visited date
    workLocationInfo: WorkLocationInfo | null; // this is user's FWH info, changes to this default value will break call to loadWorkLocationInfo
    collaboratorsLocations: Map<string, Map<CollaboratorGroup, UserEmailAddress[]>> | null;
    /* converting to null/unknown first to know when data has been loaded already
    Stores collaborators with location info with building id as a key.
    ex. < BUILDING_ID : <'Saved' : [EMAIL_ADDRESS]>> */ rareVisitData: Map<
        UserEmailAddress,
        string[]
    >; // Map of rare visit data, key is collaborators' email and value is array of building Id's
    shouldFilterByRareVisits: boolean;
    buildingAppDefinitions: Map<string, AppDefinition[]>;
} =
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function getStore called at module scope is not allowed. This may cause unwanted side effects. */
    getStore();

const dest = createAccessors('places-explore-app-store', getStore);
export const getCurrentDateOrNull: () => OwaDate | null = dest.getCurrentDate;
export const setCurrentDate: (newValue: OwaDate | null) => void = dest.setCurrentDate;
export const setWorkLocationInfo: (newValue: WorkLocationInfo | null) => void =
    dest.setWorkLocationInfo;
export const getWorkLocationInfo: () => WorkLocationInfo | null = dest.getWorkLocationInfo;
export const setCollaboratorsLocations: (
    newValue: Map<string, Map<CollaboratorGroup, UserEmailAddress[]>> | null
) => void = dest.setCollaboratorsLocations;
export const getCollaboratorsLocations: () => Map<
    string,
    Map<CollaboratorGroup, UserEmailAddress[]>
> | null = dest.getCollaboratorsLocations;
export const getRecentBuildings: () => string[] | null = dest.getRecentBuildings;
export const getRareVisitData: () => Map<UserEmailAddress, string[]> = dest.getRareVisitData;
export const getShouldFilterByRareVisits: () => boolean = dest.getShouldFilterByRareVisits;
export const setShouldFilterByRareVisits: (newValue: boolean) => void =
    dest.setShouldFilterByRareVisits;
export const getBuildingAppDefinitions: () => Map<string, AppDefinition[]> =
    dest.getBuildingAppDefinitions;

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setRecentBuildings: (recentBuildings: string[]) => void = mutatorAction(
    'SET_RECENT_BUILDINGS',
    (recentBuildings: string[]): void => {
        store.recentBuildings = recentBuildings;
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const addRareVisitData: (email: UserEmailAddress, buildingIds: string[]) => void =
    mutatorAction('ADD_RARE_VISIT_DATA', (email: UserEmailAddress, buildingIds: string[]): void => {
        store.rareVisitData.set(email, buildingIds);
    });

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setBuildingAppDefinitions: (buildingId: string, apps: AppDefinition[]) => void =
    mutatorAction(
        'SET_BUILDING_APP_DEFINITIONS',
        (buildingId: string, apps: AppDefinition[]): void => {
            store.buildingAppDefinitions.set(buildingId, apps);
        }
    );
